import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTheme } from 'styled-components';
import { SocketContext } from 'src/context/SocketContext';
import { Flex, Modal, Text, Textarea, Button } from 'src/components/common';
import { RootState, useAppSelector } from 'src/store';
import { userAPI } from 'src/api/user/ApiRequests';
import { ToastNotifications } from 'src/utils/toast-notifications';
import { parseError } from 'src/utils/error-parser';
import { Message } from 'src/types/Message';
import { User } from 'src/types/User';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'src/utils/date-parser';

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetUser: User;
}

export const ChatModal = ({ isOpen, onClose, targetUser }: ChatModalProps) => {
  const theme = useTheme();
  const { socket, reloadSocket } = useContext(SocketContext);

  const { t } = useTranslation();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const lastMessageRef = useRef(null);

  const loadMessages = async () => {
    try {
      const response = await userAPI.getMessages(user.id, targetUser.id);
      setMessages(response.data ?? []);
    } catch (error) {
      const err = parseError(error);
      ToastNotifications.error(err, 'loadChatMessages');
    }
  };

  useEffect(() => {
    if (!user) return;

    reloadSocket();
  }, [user]);

  useEffect(() => {
    if (!targetUser || !user) return;

    loadMessages();
  }, [targetUser]);

  useEffect(() => {
    const handler = (data) => {
      setMessages([...messages, data]);
    };

    socket.off('updateChat', handler).on('updateChat', handler);
  }, [socket, messages]);

  useEffect(() => {
    lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const messageChanged = (e) => {
    setNewMessage(e.target.value);
  };

  const sendNewMessageClicked = () => {
    socket.emit('newMessage', {
      message: newMessage.trim(),
      fromUserId: user.id,
      toUserId: targetUser.id,
      socketId: `${socket.id}+${Math.random()}`,
    });
    setNewMessage('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium" backgroundColor={theme.colors.background.secondary.BG4}>
      <Flex height={!messages || messages.length == 0 ? '0%' : '500px'} flexDirection={'column'}>
        <Text
          variant={'heading6'}
          text={`${t('messages_4')} ${targetUser.username}`}
          margin={'32px 0 48px 0'}
          color={theme.colors.palette.purple}
        />
        <Flex
          width={'100%'}
          height={!messages || messages.length == 0 ? '0%' : '100%'}
          flexDirection={'column'}
          gap="20px"
          overflow={'scroll'}
        >
          {messages &&
            messages.map((message: Message, index) => (
              <Flex key={index} width={'100%'} justifyContent={message.fromUser.id === user.id ? 'flex-end' : 'flex-start'}>
                <Flex
                  maxWidth={'80%'}
                  border={`1px solid ${theme.colors.border.darker}`}
                  borderRadius="20px"
                  backgroundColor={theme.colors.border.darker}
                  padding={'10px'}
                  flexDirection={'column'}
                  gap="5px"
                >
                  <Text variant={'paragraph1'} text={message.message} />
                  <Text
                    variant={'paragraph4'}
                    text={formatDate(message.created_at)}
                    textAlign={message.fromUser.id === user.id ? 'right' : 'left'}
                    width={'100%'}
                  />
                </Flex>
              </Flex>
            ))}
          <Flex ref={lastMessageRef} />
        </Flex>
        <Flex width={'100%'} flexDirection={'column'} alignItems={'center'} gap="20px">
          <Textarea placeholder={t('hint_message')} value={newMessage} width="100%" onChange={messageChanged} />
          <Button variant={'primaryDefault'} text={t('btn_send')} width="100%" onClick={() => sendNewMessageClicked()} />
        </Flex>
        <Text variant={'paragraph3'} text={t('messages_5')} textAlign="center" width={'100%'} margin={'8px 0 0 0'} />
      </Flex>
    </Modal>
  );
};
